import React, { lazy, Suspense, type ComponentType, type UIEvent } from 'react'
import { ListItem, useOverlay, useInfoSheet } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  ContentAttached,
  ContentDelete,
  ContentFlag,
  ContentPrint,
  ControlSplit,
  HelpAnnouncement,
  MenuSettings,
  NoticeQuestion,
  PersonDuplicates,
  PersonFamily,
  PersonSearch,
} from '@fs/zion-icon'
import { getFSTreesUrl } from '@fs/zion-search-partners'
import { trackEvent } from '@fs/zion-analytics'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useFeatureFlag } from '@fs/zion-flags'
import { buildLangUrl } from '@fs/zion-router'
import { usePermission } from '@fs/zion-permissions'
import { usePersonPageParams } from '../../personPageRouteUtil'
import { useResearchHelpContext } from '../../contexts/ResearchHelpContext'
import { useFocusFamilyMembersContext } from '../../contexts/FocusFamilyMembersContext'
import SettingsInfoSheet from '../settings-info-sheet/SettingsInfoSheet'
import useDeleteConstraint from '../../../data-service/useDeleteConstraint'
import useFocusPerson from '../../../data-service/useFocusPerson'
import SourcesInfoSheet from '../info-sheets/SourcesInfoSheet'
import PossibleDuplicatesNotAvailableDialog from './PossibleDuplicatesNotAvailableDialog'

const ReportAbuseOverlay = lazy(() => import('@fs/zion-report-abuse-overlay'))
const MergeByIdDialog = lazy(() => import('./MergeByIdDialog'))
const DeletePersonDialog = lazy(() => import('./DeletePersonDialog'))
const PrintDialog = lazy(() => import('./PrintDialog'))

const WHATS_NEW_HELP_URL = 'https://www.familysearch.org/help/helpcenter/article/family-tree-whats-new'
const DELETE_PERSON_HELP_URL =
  'https://www.familysearch.org/help/helpcenter/article/why-cant-i-delete-this-person-in-family-tree'

// Opens a help center article in a new tab. `opener` is cleared after opening rather than passing a 'noopener'
// feature string, because any non-empty feature string makes some browsers open a popup window instead of a tab.
function openHelpArticle(url: string): void {
  const helpWindow = window.open(buildLangUrl(url), '_blank')
  if (helpWindow) helpWindow.opener = null
}

type Props = {
  hideIcons?: boolean
  showLayoutSettings?: boolean
}

function startIcon(Icon: ComponentType, hideIcons: boolean): JSX.Element | undefined {
  if (hideIcons) return undefined
  return <ListItem.Icon Icon={Icon} />
}

// These items are exported without being wrapped in a List so they can be reused in the MobileMenu component
export default function ToolsMenuItems({ hideIcons = false, showLayoutSettings }: Props): JSX.Element | null {
  const [t] = useTranslation()
  const { pid } = usePersonPageParams('full')
  const { focusPerson } = useFocusPerson()

  const mergeByIdOverlay = useOverlay()
  const deletePersonOverlay = useOverlay()
  const reportAbuseOverlay = useOverlay()
  const possibleDuplicatesNotAvailableOverlay = useOverlay()
  const printOverlay = useOverlay()
  const settingsInfoSheet = useInfoSheet()
  const sourcesInfoSheet = useInfoSheet()

  const { deleteConstraint } = useDeleteConstraint(pid)
  const canDelete = deleteConstraint && deleteConstraint.personDeleteConstraint === 'CAN_DELETE_PERSON'

  const { focusFamilyMembers } = useFocusFamilyMembersContext()
  const { researchHelpData } = useResearchHelpContext()
  const { isResearcherGroup } = useGroupsContext() ?? {}
  const [canModifyPrivateSpaceEntities] = usePermission('ModifyAllPrivateSpaceEntitiesPermission')
  const showAdvancedUnmergeButton =
    useFeatureFlag('treeWeb_personR9_advancedUnmerge').isOn && canModifyPrivateSpaceEntities

  const possibleDuplicatesCount = researchHelpData.duplicatePersonDataProblems?.length ?? 0

  if (!focusPerson || !focusFamilyMembers) return null
  return (
    <>
      {showLayoutSettings && (
        <ListItem
          data-testid="tool-mylayoutsettings"
          dense
          linkName="Tools: My Layout Settings"
          onClick={settingsInfoSheet.handleClick}
          startElement={startIcon(MenuSettings, hideIcons)}
          primaryText={t('tree-person.layout-settings.label')}
        />
      )}

      <ListItem
        data-testid="tool-sourcessheet"
        dense
        linkName="Tools: Sources Sheet"
        onClick={sourcesInfoSheet.handleClick}
        startElement={startIcon(ContentAttached, hideIcons)}
        primaryText={`${t('tree-person.sources.header')} (${focusPerson?.sourceCount})`}
      />

      <ListItem
        data-testid="tool-whats-new-dialog"
        dense
        linkName="Tools: What's New Dialog"
        onClick={() => openHelpArticle(WHATS_NEW_HELP_URL)}
        startElement={startIcon(HelpAnnouncement, hideIcons)}
        primaryText={t('tree-person.whats-new.heading')}
      />

      {!focusPerson.readOnly && (
        <ListItem
          data-testid="tool-possibleduplicates"
          dense
          linkName="Tools: Possible Duplicates"
          onClick={
            !focusPerson.visibleToAllWhenUsingFamilySearchApps
              ? possibleDuplicatesNotAvailableOverlay.handleClick
              : undefined
          }
          to={focusPerson.visibleToAllWhenUsingFamilySearchApps ? `/research-help/${pid}` : undefined}
          startElement={startIcon(PersonDuplicates, hideIcons)}
          primaryText={`${t('tree-person.possible-duplicates.label')} (${possibleDuplicatesCount})`}
        />
      )}

      <ListItem
        data-testid="tool-findsimilarpeople"
        dense
        disabled={isResearcherGroup}
        linkName="Tools: Find Similar People"
        to={getFSTreesUrl({ person: focusPerson, familyMembers: focusFamilyMembers })}
        external
        target="_blank"
        startElement={startIcon(PersonSearch, hideIcons)}
        primaryText={t('tree-person.find-similar-people.label')}
      />

      <ListItem
        data-testid="tool-mergebyid"
        dense
        onClick={(e) => {
          trackEvent({ link_name: 'Tools: Merge By ID' })
          mergeByIdOverlay.handleClick(e)
        }}
        startElement={startIcon(PersonFamily, hideIcons)}
        primaryText={t('tree-person.merge-by-id.label')}
        disabled={focusPerson.readOnly}
      />

      <ListItem
        data-testid="tool-reportabuse"
        dense
        onClick={(event) => {
          trackEvent({ link_name: 'Tools: Report Abuse' })
          reportAbuseOverlay.handleClick(event)
        }}
        startElement={startIcon(ContentFlag, hideIcons)}
        primaryText={t('tree-person.report-abuse.label')}
      />

      <ListItem
        data-testid="tool-deleteperson"
        dense
        disabled={!canDelete}
        onClick={(event) => {
          trackEvent({ link_name: 'Tools: Delete Person' })
          deletePersonOverlay.handleClick(event)
        }}
        startElement={startIcon(ContentDelete, hideIcons)}
        primaryText={t('tree-person.delete-person.label')}
        endElement={
          canDelete ? undefined : (
            <ListItem.IconButton
              Icon={NoticeQuestion}
              title={t('tree-person.delete-person-unavailable.header')}
              data-testid="why-no-can-delete-button"
              onClick={() => openHelpArticle(DELETE_PERSON_HELP_URL)}
            />
          )
        }
      />

      <ListItem
        data-testid="tool-print"
        dense
        onClick={(event: UIEvent) => {
          trackEvent({ link_name: 'Tools: Print' })
          printOverlay.handleClick(event)
        }}
        startElement={startIcon(ContentPrint, hideIcons)}
        primaryText={t('tree-person.print-options.label')}
      />

      {showAdvancedUnmergeButton && (
        <ListItem
          data-testid="advanced-unmerge-button"
          dense
          to={`/unmerge/${pid}`}
          startElement={startIcon(ControlSplit, hideIcons)}
          primaryText={t('tree-person.advanced-unmerge.label')}
        />
      )}

      <Suspense fallback="">
        {(mergeByIdOverlay.isOpen || mergeByIdOverlay.transitioning) && <MergeByIdDialog overlay={mergeByIdOverlay} />}
        {(deletePersonOverlay.isOpen || deletePersonOverlay.transitioning) && (
          <DeletePersonDialog overlay={deletePersonOverlay} />
        )}
        {(possibleDuplicatesNotAvailableOverlay.isOpen || possibleDuplicatesNotAvailableOverlay.transitioning) && (
          <PossibleDuplicatesNotAvailableDialog overlay={possibleDuplicatesNotAvailableOverlay} />
        )}
        {(reportAbuseOverlay.isOpen || reportAbuseOverlay.transitioning) && (
          <ReportAbuseOverlay
            overlay={reportAbuseOverlay}
            subject={`Person Details: ${focusPerson?.name} | ${pid}`}
            additionalInfo={`
      Person ID: ${pid}
      Person Name: ${focusPerson?.name}
      URL: ${window.location.href}
      `}
          />
        )}
        {(printOverlay.isOpen || printOverlay.transitioning) && <PrintDialog overlay={printOverlay} />}
      </Suspense>
      {showLayoutSettings && <SettingsInfoSheet infoSheetConfig={settingsInfoSheet} />}
      <SourcesInfoSheet infoSheet={sourcesInfoSheet} />
    </>
  )
}
